import React, { useMemo, useState, useCallback } from 'react';
import { Card, Text, Button } from 'v3/components';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LinkIcon from '@mui/icons-material/Link';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { styles } from './styles';

const BASE_URL_PUBLIC_CHECKLIST =
  import.meta.env.VITE_BASE_URL_PUBLIC_CHECKLIST ||
  import.meta.env.VITE_REDIRECR_URI ||
  'http://localhost:3000';

function buildPublicUrl(linkObj) {
  if (!linkObj) return '';
  if (linkObj.url) return linkObj.url;
  if (linkObj.token) return `${BASE_URL_PUBLIC_CHECKLIST}/checklist/public/${linkObj.token}`;
  return '';
}

function copyToClipboard(text) {
  if (!text) return Promise.resolve(false);

  if (navigator.clipboard?.writeText) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return Promise.resolve(!!ok);
  } catch (e) {
    return Promise.resolve(false);
  }
}

export default function LinkCreatedCard({ link, onClose, onCopy }) {
  const url = useMemo(() => buildPublicUrl(link), [link]);

  const expireDays = Number(link?.expirationDays ?? 0);
  const singleUse = !!link?.responseUnique;
  const anonymous = !!link?.isAnonymous;

  const chips = [];

  chips.push({
    key: 'expire',
    icon: <AccessTimeOutlinedIcon style={{ fontSize: 16, color: '#111827' }} />,
    label: expireDays > 0 ? `Expira em ${expireDays} dias` : 'Sem expiração',
  });

  if (singleUse) {
    chips.push({
      key: 'single',
      icon: <InfoOutlinedIcon style={{ fontSize: 16, color: '#111827' }} />,
      label: 'Uso único',
    });
  }

  if (anonymous) {
    chips.push({
      key: 'anon',
      icon: <PersonOutlineIcon style={{ fontSize: 16, color: '#111827' }} />,
      label: 'Resposta anônima',
    });
  }

  const [copied, setCopied] = useState(false);

  const triggerCopied = useCallback(() => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      triggerCopied();
      if (onCopy) onCopy(url);

    }
  }, [url, onCopy, triggerCopied]);

  return (
    <>
      <Card style={styles.card}>
        <div style={styles.successRow}>
          <div style={styles.successIconBox}>
            <CheckCircleOutlineIcon style={{ fontSize: 16, color: '#111827' }} />
          </div>

          <Text type="regular" weight={700} color="dark">
            Link Gerado com Sucesso!
          </Text>
        </div>

        <div style={styles.divider} />

        <div style={styles.linkRow}>
          <input readOnly value={url || ''} style={styles.linkInput} />
          <button
            type="button"
            onClick={handleCopy}
            style={{
              ...styles.copyBtn,
              ...(copied ? styles.copyBtnCopied : null),
            }}
            aria-label="Copiar link"
            title={copied ? 'Copiado!' : 'Copiar link'}
          >
            {copied ? (
              <CheckRoundedIcon style={{ fontSize: 18, color: '#111827' }} />
            ) : (
              <ContentCopyOutlinedIcon style={{ fontSize: 18, color: '#111827' }} />
            )}
          </button>
        </div>

        <div style={styles.chipsRow}>
          {chips.map((c) => (
            <span key={c.key} style={styles.chip}>
              {c.icon}
              {c.label}
            </span>
          ))}
        </div>
      </Card>

      <div style={styles.footer}>
        <Button variant="secondary" onClick={onClose} style={{ minWidth: 120 }}>
          <Text type="regular" weight={500}>
            Fechar
          </Text>
        </Button>

        <Button onClick={handleCopy} style={{ minWidth: 160 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {copied ? <CheckRoundedIcon style={{ fontSize: 18 }} /> : <LinkIcon style={{ fontSize: 18 }} />}
            <Text type="regular" weight={500}>
              {copied ? 'Copiado!' : 'Copiar Link'}
            </Text>
          </div>
        </Button>
      </div>
    </>
  );
}
